import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAxesTooltip } from '@mui/x-charts/ChartsTooltip';
import { LineChart } from '@mui/x-charts/LineChart';
import { useMemo } from 'react';
import { formatNumberAbbreviated } from '../../../utils/number';
import { copilotChartContainer, copilotSectionTitle } from './styles';
import type { LineConfig } from './types';

type CopilotLineChartProps = {
  title?: string;
  dataset: Record<string, string | number>[];
  xAxisKey: string;
  lines: LineConfig[];
  height?: number;
};

const axisTickStyle = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: 10,
  fill: '#5f6978',
} as const;

function ChartTooltip() {
  const tooltipDataList = useAxesTooltip();
  if (!tooltipDataList || tooltipDataList.length === 0) return null;

  const tooltipData = tooltipDataList[0];
  const { axisValue, seriesItems } = tooltipData;

  return (
    <Box
      sx={{
        bgcolor: '#EEF2FF',
        border: '1px solid #C4D5FF',
        borderRadius: '8px',
        px: 1.5,
        py: 1,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <Typography sx={{ fontSize: 11, color: '#7e8796', fontFamily: 'inherit', mb: 0.5 }}>
        {String(axisValue)}
      </Typography>
      {seriesItems.map((s) => {
        if (s.value == null) return null;
        return (
          <Box key={String(s.seriesId)} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#020f20', fontFamily: 'inherit' }}>
              {formatNumberAbbreviated(s.value as number)}
            </Typography>
            {s.formattedLabel && (
              <Typography sx={{ fontSize: 11, color: '#7e8796', fontFamily: 'inherit' }}>
                {s.formattedLabel}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export function CopilotLineChart({ title, dataset, xAxisKey, lines, height = 180 }: CopilotLineChartProps) {
  if (dataset.length === 0) return null;

  const showLegend = lines.length > 1;
  const tickInterval = dataset.length <= 12 ? 1 : Math.ceil(dataset.length / 8);

  const xAxis = useMemo(
    () => [
      {
        scaleType: 'point' as const,
        dataKey: xAxisKey,
        tickLabelStyle: axisTickStyle,
        tickSize: 0,
        tickInterval: (_: unknown, i: number) => i % tickInterval === 0,
      },
    ],
    [xAxisKey, tickInterval],
  );

  const series = useMemo(
    () =>
      lines.map((line) => ({
        dataKey: line.dataKey,
        label: line.label,
        color: line.color,
        showMark: false,
      })),
    [lines],
  );

  const yAxis = useMemo(
    () => [
      {
        valueFormatter: (v: number) => formatNumberAbbreviated(v),
        tickLabelStyle: axisTickStyle,
        tickSize: 0,
      },
    ],
    [],
  );

  return (
    <Box>
      <Typography sx={copilotSectionTitle}>{title ?? 'Trend'}</Typography>
      <Box sx={copilotChartContainer}>
      {showLegend && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mb: '8px' }}>
          {lines.map((line) => (
            <Box key={line.dataKey} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '2px',
                  bgcolor: line.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: 10,
                  lineHeight: '14px',
                  color: '#5f6978',
                }}
              >
                {line.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      <LineChart
        dataset={dataset}
        xAxis={xAxis}
        yAxis={yAxis}
        series={series}
        height={height}
        margin={{ left: 40, right: 8, top: 8, bottom: 24 }}
        grid={{ horizontal: true }}
        slots={{ tooltip: ChartTooltip }}
        sx={{
          '& .MuiLineElement-root': { strokeWidth: 2 },
          '& .MuiChartsGrid-horizontalLine': { stroke: '#e8ebf2', strokeDasharray: 'none' },
          '& .MuiChartsAxis-line': { display: 'none' },
          '& .MuiChartsAxis-tick': { display: 'none' },
        }}
        hideLegend={true}
      />
      </Box>
    </Box>
  );
}
